import { isAxiosError } from 'axios';

import { endpointsV3 } from '@/config/api';
import { apiV3 } from '@/lib/api-client';
import { firebaseAuth } from '@/lib/firebase';

import type { Creator, RegisterPayload, UpdateCreatorPayload } from '../types';
import { deriveOrganizationName } from '../utils/organization-name';

export const getCreator = (): Promise<Creator> => {
  return apiV3.get(endpointsV3.creator);
};

export const registerCreator = ({
  creatorName,
  organizationName,
  organizationInviteId,
}: RegisterPayload): Promise<void> => {
  return apiV3.post(
    endpointsV3.register,
    { creator_name: creatorName, organization_name: organizationName },
    {
      params: organizationInviteId
        ? { organization_invite_id: organizationInviteId }
        : undefined,
    },
  );
};

const isCreatorNotFoundError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

let creatorInitPromise: Promise<{ creator: Creator; isNew: boolean }> | null =
  null;
let creatorInitUid: string | null = null;

export const getOrCreateCreator = async (): Promise<{
  creator: Creator;
  isNew: boolean;
}> => {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error('Not authenticated');

  if (creatorInitPromise && creatorInitUid === user.uid)
    return creatorInitPromise;

  creatorInitUid = user.uid;
  const promise = (async () => {
    try {
      const creator = await getCreator();
      return { creator, isNew: false };
    } catch (error) {
      if (!isCreatorNotFoundError(error)) throw error;
      await registerCreator({
        creatorName: user.displayName || null,
        organizationName: deriveOrganizationName(
          user.displayName,
          user.email || '',
        ),
      });
      const creator = await getCreator();
      return { creator, isNew: true };
    }
  })().finally(() => {
    if (creatorInitPromise === promise) {
      creatorInitPromise = null;
      creatorInitUid = null;
    }
  });
  creatorInitPromise = promise;

  return promise;
};

export const updateCreator = (data: UpdateCreatorPayload): Promise<void> => {
  return apiV3.patch(endpointsV3.creator, data);
};
